<template>
  <section>
    <b-col v-if="loading">
      <b-skeleton
        animation="wave"
        class="mx-auto"
        width="100%"
        height="100px"
      />
      <b-skeleton
        animation="wave"
        class="mx-auto"
        width="100%"
        height="500px"
      />
    </b-col>
    <scroll-container
      v-else
      class="pt-2 mt-n30px"
      height-offset="146px"
    >
      <b-card class="mb-1">
        <b-form-group
          class="mb-0"
          :label="$tk('ClientPortal.Placement.TemplateName')"
          label-for="Name"
          :invalid-feedback="errorMessages['Name']"
        >
          <b-form-input
            id="Name"
            v-model="wrapper.template.name"
            :placeholder="$tk('ClientPortal.Placement.TemplateNamePlaceholder')"
            :state="errorMessages['Name'] == null ? null : false"
          />
        </b-form-group>
      </b-card>
      <b-card>
        <div class="d-flex justify-content-between mb-1">
          <p>{{ $tk('ClientPortal.Placement.Preview') }}: {{ deviceSelected.name }} ({{ deviceSelected.width }}x{{ deviceSelected.height }})</p>
          <div>
            <b-button
              v-for="device in devices"
              :key="device.id"
              variant="outline-dark ml-50"
              size="sm"
              @click="updatePreview(device.id)"
            >
              <GjIcon
                class="mr-50"
                :class="{'text-primary': device.id === deviceSelected.id }"
                :name="device.name"
              />
              {{ device.name }}
            </b-button>
          </div>
        </div>
        <div class="border bg-light text-center p-2">
          <iframe
            :id="'templatePreview'"
            :ref="'templatePreview'"
            class="border-0 bg-white"
            :style="{width: deviceSelected.width + 'px', height: deviceSelected.height + 'px'}"
          />
        </div>
        <span
          v-if="errorMessages['HtmlContent']"
          class="field-validation-valid text-danger small"
        >
          {{ errorMessages['HtmlContent'] }}
        </span>
      </b-card>
      <b-card>
        <h6>
          {{ $tk('ClientPortal.NativeAdTemplate.Editor') }}
        </h6>
        <AceEditor
          ref="aceEditor"
          v-model="wrapper.template.htmlContent"
          :value="wrapper.template.htmlContent"
          lang="javascript"
          theme="github"
          width="100%"
          height="300px"
          :options="options"
          :commands="commands"
          @init="editorInit"
        />
      </b-card>
    </scroll-container>
    <div class="border-top text-right py-1">
      <b-button
        class="bg-white"
        size="sm"
        variant="outline-dark"
        :disabled="saving"
        :to="`/${$route.params.accountId}/nativeadtemplate`"
      >
        {{ $tk('Form.Actions.Cancel') }}
      </b-button>
      <b-button
        class="ml-50"
        size="sm"
        variant="primary"
        :disabled="saving"
        @click.prevent="saveTemplate(wrapper.template)"
      >
        {{ $tk('ClientPortal.Form.Actions.Save') }}
      </b-button>
    </div>
  </section>
</template>

<script>
import GjIcon from '@gjirafatech/gjirafa-icons/Icon.vue'
import AceEditor from 'vuejs-ace-editor'
import ScrollContainer from '@/components/ScrollContainer.vue'
import NativeAdTemplatesService from '@/api/client/nativeAdTemplates.service'
import notification from '@/mixins/toastification'
import scrollToError from '@/mixins/scrollToError'

export default {
  components: {
    GjIcon,
    AceEditor,
    ScrollContainer,
  },
  mixins: [notification],
  props: {
    // eslint-disable-next-line vue/require-default-prop
    templateId: String,
  },
  data() {
    return {
      wrapper: {},
      loading: false,
      loadingHtmlContent: false,
      saving: false,
      errorMessages: {},
      tabIndex: '',
      deviceSelected: {
        id: 3, screen: 'desktop', name: 'Desktop', height: 736, width: 1024,
      },
      devices:
      [{
        id: 3, screen: 'desktop', name: 'Desktop', height: 736, width: 1024,
      }, {
        id: 2, screen: 'tablet', name: 'Tablet', height: 1024, width: 768,
      }, {
        id: 1, screen: 'mobile', name: 'Mobile', height: 736, width: 414,
      }],
      content: '',
      options: {
        enableBasicAutocompletion: true,
        enableLiveAutocompletion: true,
        fontSize: 14,
        highlightActiveLine: true,
        enableSnippets: true,
        showLineNumbers: true,
        tabSize: 2,
        showPrintMargin: false,
        showGutter: true,
      },
      commands: [
        {
          name: 'save',
          bindKey: { win: 'Ctrl-s', mac: 'Command-s' },
          // exec: this.updatePreview(this.deviceSelected.id),
          readOnly: true,
        },
      ],
    }
  },
  watch: {
    // eslint-disable-next-line func-names
    'wrapper.template.htmlContent': function () {
      if (this.loadingHtmlContent) {
        this.updatePreview(this.deviceSelected.id)
      }
    },
  },
  async mounted() {
    this.loading = true
    try {
      const response = await NativeAdTemplatesService.getTemplate(this.templateId || 0, this.$route.params.accountId)
      this.wrapper = response
    } catch (error) {
      this.error(error.message)
    }
    this.loading = false
    this.$nextTick(() => {
      this.updatePreview(this.deviceSelected.id)
      this.loadingHtmlContent = true
    })
  },
  methods: {
    updatePreview(id) {
      this.deviceSelected = this.devices.find(x => x.id === id)
      let value = this.$refs.aceEditor.contentBackup
      this.wrapper.template.htmlContent = value
      this.wrapper.properties.forEach(x => {
        if (x.required && value.indexOf(`{{${x.propertyName}}}`) >= 0) { value = value.split(`{{${x.propertyName}}}`).join(x.previewValue) }
      })
      if (this.deviceSelected.screen === 'mobile') {
        this.$refs.templatePreview.srcdoc = `<div class="mobile">${value}</div>`
      } else {
        this.$refs.templatePreview.srcdoc = value
      }
    },
    editorInit() {
      // eslint-disable-next-line global-require
      require('brace/ext/language_tools') // language extension prerequsite...
      // eslint-disable-next-line global-require
      require('brace/mode/markdown')
      // eslint-disable-next-line global-require
      require('brace/mode/javascript') // language
      // eslint-disable-next-line global-require
      require('brace/mode/less')
      // eslint-disable-next-line global-require
      require('brace/theme/github')
      // eslint-disable-next-line global-require
      require('brace/snippets/javascript') // snippet
    },
    async saveTemplate(template) {
      this.saving = true
      this.errorMessages = {}
      try {
        const response = await NativeAdTemplatesService.saveTemplate(template, this.$route.params.accountId)
        if (response.saved) {
          this.$router.push(`/${this.$route.params.accountId}/nativeadtemplate`)
        } else {
          this.error(this.$tk('ClientPortal.Placement.FailedToSaveTemplate'))
          this.errorMessages = response.data
        }
      } catch (error) {
        this.error(error.message)
      } finally {
        scrollToError(this.errorMessages)
        this.saving = false
      }
    },
  },
}
</script>
